import {
  Dropdown,
  Option,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  deriveCommission,
  payrollPeriodFor,
  shiftPayrollPeriod,
  todayInZone,
  type CommissionReport,
  type PayrollPeriod,
} from '@worklogix/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SHRINKABLE } from '../../responsive';
import { useTimeAdminApi } from '../api';
import { DataTable, type DataColumn } from '../components/DataTable';
import { toErrorMessage } from '../errors';
import { useIsAdmin } from '../useIsAdmin';

import { PostToNetSuite, type PostPlanView, type PostResultView } from './PostToNetSuite';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    ...SHRINKABLE,
  },
  field: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  totals: { color: tokens.colorNeutralForeground2 },
  note: { color: tokens.colorNeutralForeground3 },
  issue: { color: tokens.colorPaletteRedForeground1 },
  amount: { fontVariantNumeric: 'tabular-nums' },
});

const money = (n: number): string =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

/**
 * Commission — READ-ONLY, read from NetSuite.
 *
 * Deliberately NOT the editable amount table the other reports use (Michael,
 * 2026-09-11: "make it read-only from the report"). Every figure comes from the
 * saved search Boe already runs, so there is nothing to type and nothing that
 * can disagree with the system of record. The API has no create/update/delete
 * for it either — this is not merely a UI that declines to offer editing.
 *
 * The period picker names the PAYROLL week; the commission itself is calculated
 * over a different week (Mon–Sun, shifted back two days), which the server
 * derives and this surfaces so the dates on screen are never ambiguous.
 */
export function CommissionReportTable(): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();
  const isAdmin = useIsAdmin();

  const [periodOffset, setPeriodOffset] = useState(0);
  const currentPeriod = useMemo<PayrollPeriod>(
    () => payrollPeriodFor(todayInZone(new Date())),
    []
  );
  const period = useMemo<PayrollPeriod>(
    () => shiftPayrollPeriod(currentPeriod, periodOffset),
    [currentPeriod, periodOffset]
  );

  const [report, setReport] = useState<CommissionReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    api
      .getCommissionReport(period.endDate)
      .then((r) => {
        if (active) {
          setReport(r);
        }
      })
      .catch((err: unknown) => {
        if (active) {
          // Never silent: a blank commission table and a failed read look
          // identical, and one of them means nobody gets paid.
          setReport(null);
          setError(toErrorMessage(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [api, period.endDate]);

  /**
   * Adapters onto the shared posting dialog.
   *
   * The period is NOT the dialog's to choose here — this screen already has a
   * picker, and two period controls disagreeing about which week is being paid
   * is exactly the mistake that costs a payroll run.
   */
  const loadPostPlan = useCallback(
    async (payPeriodEnding: string): Promise<PostPlanView> => {
      const plan = await api.getCommissionPostPreview(payPeriodEnding);
      return {
        batchNumber: plan.batchNumber,
        totalToPost: plan.totalToPost,
        rows: plan.rows.map((r) => ({
          key: r.nsEmployeeId,
          name: r.employeeName,
          amount: r.commission,
          action: r.action,
          reason: r.reason,
        })),
      };
    },
    [api]
  );

  const runPost = useCallback(
    async (payPeriodEnding: string): Promise<PostResultView> => {
      const result = await api.postCommission(payPeriodEnding);
      return {
        posted: result.posted.map((p) => ({ name: p.employeeName, amount: p.commission })),
        failed: result.failed.map((f) => ({ name: f.employeeName, reason: f.reason })),
      };
    },
    [api]
  );

  const columns: ReadonlyArray<DataColumn<CommissionReport['rows'][number]>> = useMemo(
    () => [
      { columnId: 'rep', header: 'Sales Rep', renderCell: (r) => r.employeeName },
      {
        columnId: 'sales',
        header: 'Sales',
        // null = the sales read failed. A dash, never $0.00 — one is "unknown",
        // the other is a claim that they sold nothing.
        renderCell: (r) => <span className={styles.amount}>{r.sales === null ? '—' : money(r.sales)}</span>,
      },
      {
        columnId: 'tons',
        header: 'Tons Sold',
        renderCell: (r) => <span className={styles.amount}>{r.tons.toFixed(2)}</span>,
      },
      {
        columnId: 'commission',
        header: 'Commission',
        renderCell: (r) => <span className={styles.amount}>{money(r.commission)}</span>,
      },
      {
        columnId: 'perTon',
        header: 'Comm/Ton',
        renderCell: (r) => <span className={styles.amount}>{money(r.commissionPerTon)}</span>,
      },
      {
        columnId: 'pct',
        header: '% Commission',
        renderCell: (r) => {
          if (r.sales === null) {
            return '—';
          }
          const { commissionRateOfSales } = deriveCommission({
            commission: r.commission,
            sales: r.sales,
            tons: r.tons,
          });
          return commissionRateOfSales === null
            ? '—'
            : `${(commissionRateOfSales * 100).toFixed(2)}%`;
        },
      },
    ],
    [styles.amount]
  );

  return (
    <div className={styles.root}>
      <div className={styles.field}>
        <Text size={200}>Pay period</Text>
        <Dropdown
          value={`${period.startDate} – ${period.endDate}${periodOffset === 0 ? ' (current)' : ''}`}
          selectedOptions={[String(periodOffset)]}
          onOptionSelect={(_, d) => setPeriodOffset(Number(d.optionValue ?? 0))}
        >
          {Array.from({ length: 9 }, (_, i) => -i).map((offset) => {
            const p = shiftPayrollPeriod(currentPeriod, offset);
            const text = `${p.startDate} – ${p.endDate}${offset === 0 ? ' (current)' : ''}`;
            return (
              <Option key={offset} value={String(offset)} text={text}>
                {text}
              </Option>
            );
          })}
        </Dropdown>
      </div>

      {report !== null && (
        // Stated explicitly because it is genuinely surprising: commission is not
        // calculated over the week it is paid in.
        <Text size={200} className={styles.note}>
          {`Commission calculated ${report.commissionWindow.startDate} – ${report.commissionWindow.endDate} (Mon–Sun), paid in the ${report.payPeriod.startDate} – ${report.payPeriod.endDate} payroll week. Figures come from the NetSuite commission report and cannot be edited here.`}
        </Text>
      )}

      {error !== null && (
        <Text className={styles.issue} role="alert">
          {`Couldn't load the commission report: ${error}`}
        </Text>
      )}

      {report !== null && report.unmatchedNsEmployeeIds.length > 0 && (
        <Text className={styles.issue} role="alert">
          {`${String(report.unmatchedNsEmployeeIds.length)} rep(s) on the NetSuite report have no WorkLogix employee record, so their commission cannot be posted from here.`}
        </Text>
      )}

      {loading ? (
        <Spinner label="Loading commission…" />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={report?.rows ?? []}
            getRowId={(r) => r.nsEmployeeId}
            emptyMessage="No commission in this pay period."
            showHeaderWhenEmpty
          />
          {report !== null && report.rows.length > 0 && (
            <Text className={styles.totals}>
              {`Total — ${money(report.totalSales)} sales · ${report.totalTons.toFixed(2)} tons · ${money(report.totalCommission)} commission`}
            </Text>
          )}
          {isAdmin && report !== null && report.rows.length > 0 && (
            // The table is read-only, but the figures still have to reach the
            // paycheck. Posting is not editing: it carries the NetSuite number
            // across unchanged, which is why it can live on a read-only report.
            <div>
              <PostToNetSuite
                what="commission"
                payPeriodEnding={period.endDate}
                loadPlan={loadPostPlan}
                post={runPost}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
